/**
 * Phase E E.4 — Modal d import DPGF Excel avec preview lots/prestations.
 *
 * Endpoint backend deja existant : POST /api/chantiers/{cid}/dpgf/import
 * (server.py:1795). Pas d endpoint /parse separe : l import est direct.
 * On affiche le retour structure (lots_count, prestations_count, lots, warnings).
 *
 * Note : la spec frontend separe parse+import en 2 endpoints, mais le backend
 * actuel les combine. On adapte l UI a la realite.
 */
import { useState } from 'react';
import axios from 'axios';
import {
  X, FileXls, Warning as WarningIcon, CheckCircle,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DPGFImportDialog({ chantierId, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dpgf/import`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        },
      );
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur import');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileXls size={24} /> Importer un DPGF Excel
          </h2>
          <button onClick={onClose} data-testid="dpgf-import-close"><X size={24} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {!result && (
            <>
              <p className="text-sm text-gray-600">
                Uploadez un fichier Excel DPGF (.xlsx / .xlsm). Le système parse automatiquement
                les colonnes Désignation, Unité, Quantité, Prix unitaire et regroupe par lot.
              </p>
              <p className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded p-2">
                ⚠️ PDF non supporté en V1. Convertir le DPGF en XLSX (Excel : Fichier → Enregistrer sous → .xlsx).
              </p>
              <input
                type="file"
                accept=".xlsx,.xlsm"
                onChange={(e) => setFile(e.target.files[0])}
                className="border rounded p-2 w-full text-sm"
                data-testid="dpgf-import-file"
              />
              {file && <p className="text-sm text-green-700">📄 {file.name}</p>}
            </>
          )}

          {result && (
            <>
              <div className={`border rounded-lg p-3 ${
                result.success
                  ? 'bg-green-50 border-green-200'
                  : 'bg-orange-50 border-orange-200'
              }`}>
                <p className="text-sm font-semibold flex items-center gap-2">
                  {result.success ? <CheckCircle size={20} className="text-green-600" /> : <WarningIcon size={20} className="text-orange-600" />}
                  {result.message || (result.success ? 'Import réussi' : 'Import partiel')}
                </p>
                <p className="text-sm mt-1">
                  <strong>{result.lots_count || 0}</strong> lots • <strong>{result.prestations_count || 0}</strong> prestations
                </p>
              </div>

              {Array.isArray(result.warnings) && result.warnings.length > 0 && (
                <div className="border border-orange-200 bg-orange-50 rounded p-3 text-sm">
                  <p className="font-medium text-orange-700 mb-1">Avertissements :</p>
                  <ul className="list-disc list-inside text-orange-700 space-y-1">
                    {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <div className="border border-red-200 bg-red-50 rounded p-3 text-sm text-red-700">
                  <p className="font-medium mb-1">Erreurs :</p>
                  <ul className="list-disc list-inside space-y-1">
                    {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}

              {/* Preview lots */}
              {Array.isArray(result.lots) && result.lots.map((lot, lotIdx) => (
                <div key={lotIdx} className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 flex items-center gap-2">
                    <span className="font-medium text-sm">{lot.nom || lot.id || `Lot ${lotIdx + 1}`}</span>
                    <span className="text-xs text-gray-600 ml-auto">
                      {(lot.postes || []).length} postes
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs text-gray-600">
                        <tr>
                          <th className="text-left p-2">Désignation</th>
                          <th className="text-left p-2">Unité</th>
                          <th className="text-right p-2">Qté</th>
                          <th className="text-right p-2">PU HT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(lot.postes || []).slice(0, 5).map((p, pIdx) => (
                          <tr key={pIdx} className="border-t">
                            <td className="p-2">{p.designation}</td>
                            <td className="p-2">{p.unite}</td>
                            <td className="p-2 text-right">{p.quantite}</td>
                            <td className="p-2 text-right">{p.prix_unitaire}</td>
                          </tr>
                        ))}
                        {(lot.postes || []).length > 5 && (
                          <tr>
                            <td colSpan={4} className="p-2 text-xs text-gray-500 italic">
                              … et {(lot.postes || []).length - 5} autres
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
              ⚠️ {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded">
            {result ? 'Fermer' : 'Annuler'}
          </button>
          {!result && (
            <button
              onClick={handleImport}
              disabled={!file || importing}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg disabled:opacity-50"
              data-testid="dpgf-import-submit"
            >
              {importing ? 'Import…' : 'Importer le DPGF'}
            </button>
          )}
          {result && (
            <button
              onClick={onSuccess}
              className="px-3 py-1.5 bg-green-600 text-white rounded-lg"
            >
              Voir les lots créés
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
